import React, { useState, useRef, useEffect } from 'react';
import { Globe, Check } from 'lucide-react';
import { useTranslation } from "react-i18next";
import { motion, AnimatePresence } from 'framer-motion';

import { useLanguage, languages } from '../../context/LanguageContext';

// ─── Flag image helper (uses flagcdn.com — always renders) ───────────────────
const FlagImg = ({ code, className = 'w-5 h-4' }) => {
    // Map language code → ISO 3166-1 alpha-2 country code
    const countryMap = {
        en: 'gb', us: 'us', ar: 'sa', fr: 'fr', de: 'de', es: 'es',
        it: 'it', pt: 'pt', ru: 'ru', zh: 'cn', ja: 'jp', ko: 'kr',
        hi: 'in', tr: 'tr', nl: 'nl', pl: 'pl', uk: 'ua', sv: 'se',
        no: 'no', da: 'dk', fi: 'fi', cs: 'cz', ro: 'ro', hu: 'hu',
        id: 'id', ms: 'my', th: 'th', vi: 'vn', he: 'il', fa: 'ir',
        ur: 'pk', bn: 'bd', ta: 'lk', sw: 'ke', af: 'za',
    };
    const country = countryMap[code] || code;
    return (
        <img
            src={`https://flagcdn.com/w40/${country}.png`}
            alt={code}
            className={`${className} rounded-sm object-cover flex-shrink-0`}
            onError={(e) => { e.target.style.display = 'none'; }}
        />
    );
};

// ─── LanguageSelector ─────────────────────────────────────────────────────────
const LanguageSelector = () => {
    const { language, setLanguage, currentLanguage } = useLanguage();
    const [isOpen, setIsOpen] = useState(false);
    const [hovered, setHovered] = useState(null);
    const dropdownRef = useRef(null);
    const { i18n } = useTranslation();

    // Close on outside click
    useEffect(() => {
        const handleClickOutside = (e) => {
            if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
                setIsOpen(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    // Close on Escape
    useEffect(() => {
        const handleKeyDown = (e) => {
            if (e.key === 'Escape') setIsOpen(false);
        };
        document.addEventListener('keydown', handleKeyDown);
        return () => document.removeEventListener('keydown', handleKeyDown);
    }, []);

    const handleLanguageSelect = (langCode) => {
        setLanguage(langCode);
        setIsOpen(false);
        i18n.changeLanguage(langCode);
    };

    return (
        <div className="relative" ref={dropdownRef}>

            {/* ── Trigger ── */}
            <button
                onClick={() => setIsOpen((v) => !v)}
                data-testid="language-selector"
                aria-label="Select language"
                aria-expanded={isOpen}
                className={`
                    group relative flex items-center gap-2 h-9 px-3 rounded-xl
                    text-[13px] font-medium tracking-wide
                    border transition-all duration-200 select-none outline-none
                    ${isOpen
                        ? 'bg-slate-900 dark:bg-white border-slate-700 dark:border-slate-200 text-white dark:text-slate-900 shadow-lg shadow-black/20'
                        : 'bg-white/60 dark:bg-slate-800/60 border-slate-200 dark:border-slate-700/60 text-slate-600 dark:text-slate-300 hover:bg-white dark:hover:bg-slate-800 hover:border-slate-300 dark:hover:border-slate-600 hover:text-slate-900 dark:hover:text-white'
                    }
                `}
            >
                {/* Animated globe icon */}
                <Globe
                    className={`w-4 h-4 flex-shrink-0 transition-all duration-300
                        ${isOpen ? 'text-blue-400 rotate-12' : 'text-slate-400 group-hover:text-slate-600 dark:group-hover:text-slate-300'}`}
                />

                {/* Current flag — md and above */}
                <span className="hidden md:flex items-center">
                    <FlagImg code={currentLanguage?.code || 'en'} className="w-5 h-3.5 rounded-sm object-cover" />
                </span>

                {/* Animated chevron */}
                <motion.svg
                    animate={{ rotate: isOpen ? 180 : 0 }}
                    transition={{ duration: 0.2, ease: 'easeInOut' }}
                    className={`hidden sm:block w-3.5 h-3.5 flex-shrink-0 transition-colors duration-200
                        ${isOpen ? 'text-slate-400 dark:text-slate-500' : 'text-slate-300 dark:text-slate-600 group-hover:text-slate-400'}`}
                    fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}
                >
                    <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
                </motion.svg>
            </button>

            {/* ── Dropdown ── */}
            <AnimatePresence>
                {isOpen && (
                    <motion.div
                        initial={{ opacity: 0, scale: 0.96, y: -6 }}
                        animate={{ opacity: 1, scale: 1, y: 0 }}
                        exit={{ opacity: 0, scale: 0.96, y: -6 }}
                        transition={{ duration: 0.15, ease: [0.16, 1, 0.3, 1] }}
                        className="
                            absolute right-0 mt-2 w-64
                            bg-white dark:bg-[#0f1117]
                            border border-slate-200/80 dark:border-slate-800
                            rounded-2xl overflow-hidden z-50
                            shadow-2xl shadow-black/10 dark:shadow-black/60
                        "
                    >
                        {/* ── Header ── */}
                        <div className="px-4 pt-4 pb-3 border-b border-slate-100 dark:border-slate-800/80">
                            <div className="flex items-center justify-between">
                                <div className="flex items-center gap-2">
                                    <div className="w-6 h-6 rounded-lg bg-gradient-to-br from-blue-500 to-cyan-400 flex items-center justify-center shadow-sm">
                                        <Globe className="w-3.5 h-3.5 text-white" />
                                    </div>
                                    <span className="text-[13px] font-semibold text-slate-800 dark:text-slate-100 tracking-tight">
                                        Language
                                    </span>
                                </div>
                                {/* Active badge */}
                                <span className="flex items-center gap-1.5 text-[11px] font-medium text-slate-400 dark:text-slate-500">
                                    <FlagImg code={currentLanguage?.code || 'en'} className="w-5 h-3.5 rounded-sm object-cover" />
                                    <span className="uppercase tracking-widest">{currentLanguage?.code}</span>
                                </span>
                            </div>
                        </div>

                        {/* ── List ── */}
                        <div className="py-1.5 max-h-72 overflow-y-auto overscroll-contain
                                        scrollbar-thin scrollbar-thumb-slate-200 dark:scrollbar-thumb-slate-700 scrollbar-track-transparent">
                            {languages.map((lang, i) => {
                                const isActive = language === lang.code;
                                return (
                                    <motion.button
                                        key={lang.code}
                                        onClick={() => handleLanguageSelect(lang.code)}
                                        onMouseEnter={() => setHovered(lang.code)}
                                        onMouseLeave={() => setHovered(null)}
                                        data-testid={`lang-option-${lang.code}`}
                                        initial={{ opacity: 0, x: -4 }}
                                        animate={{ opacity: 1, x: 0 }}
                                        transition={{ delay: i * 0.025, duration: 0.15 }}
                                        className={`
                                            w-full flex items-center justify-between
                                            px-3 py-2.5 mx-1.5 rounded-xl text-left
                                            transition-all duration-150 cursor-pointer outline-none
                                            ${isActive
                                                ? 'bg-slate-900 dark:bg-white text-white dark:text-slate-900'
                                                : 'hover:bg-slate-50 dark:hover:bg-slate-800/80 text-slate-700 dark:text-slate-300'
                                            }
                                        `}
                                        style={{ width: 'calc(100% - 12px)' }}
                                    >
                                        <div className="flex items-center gap-3 min-w-0">
                                            {/* Flag image */}
                                            <FlagImg
                                                code={lang.code}
                                                className={`w-6 h-4 rounded-sm object-cover flex-shrink-0 transition-transform duration-150
                                                    ${hovered === lang.code && !isActive ? 'scale-110' : 'scale-100'}`}
                                            />

                                            {/* Country name only */}
                                            <span className={`text-[13px] font-medium truncate leading-tight
                                                ${isActive ? 'text-white dark:text-slate-900' : 'text-slate-800 dark:text-slate-100'}`}>
                                                {lang.name}
                                            </span>
                                        </div>

                                        {/* Checkmark */}
                                        {isActive && (
                                            <motion.div
                                                initial={{ scale: 0, opacity: 0 }}
                                                animate={{ scale: 1, opacity: 1 }}
                                                className="flex-shrink-0 w-5 h-5 rounded-full bg-white/20 dark:bg-black/10 flex items-center justify-center ml-2"
                                            >
                                                <Check className="w-3 h-3 text-white dark:text-slate-900 stroke-[3]" />
                                            </motion.div>
                                        )}
                                    </motion.button>
                                );
                            })}
                        </div>

                        {/* ── Footer ── */}
                        <div className="px-4 py-2.5 border-t border-slate-100 dark:border-slate-800/80 bg-slate-50/60 dark:bg-slate-900/40">
                            <p className="text-[11px] text-slate-400 dark:text-slate-600 font-medium">
                                {languages.length} languages available
                            </p>
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>
        </div>
    );
};

export default LanguageSelector;